// @flow
import * as React from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {
  NewCardPaymentDemo,
  PaymentElementDemo,
  Image,
  ChartIconGroup,
  ChartIcon,
} from 'docs/components';
import {defineMessages, useIntl} from 'react-intl';
import CheckoutPreviewImage from 'docs/images/components/demo/checkout-preview.png';
import type {IconColor} from 'sail/Icon/types';

import './ComplexityCallout.scss';

const messages = defineMessages({
  complexity: {
    id: 'docsShell.complexityCallout.label',
    description:
      'A label displayed before a series of dots representing how complex a guide is to integrate (e.g. 1 dot is easy, 5 dots is very hard)',
    defaultMessage: 'Complexity',
  },
  complexityAriaLabel: {
    id: 'docsShell.complexityCallout.ariaLabel',
    description:
      'A label shown to screen readers that explains how complex a guide is to integrate (e.g. 1 dot is easy, 5 dots is very hard)',
    defaultMessage: `This integration has a complexity score of {complexity} out of 5. A score of 1 means it's easy to integrate while a score of 5 means it's difficult.`,
  },
});

type ComplexityCalloutProps = {|
  complexity: number,
  demo: 'elements' | 'checkout',
  image?: string,
  isHorizontal: boolean,
  color?: IconColor,
  children: React.Node,
|};

type CheckoutPreviewProps = {|
  image?: string,
|};

const CheckoutPreview = ({image}: CheckoutPreviewProps) => {
  return (
    <a href="https://checkout.stripe.dev/" target="_blank">
      <img src={image || CheckoutPreviewImage} alt="" />
    </a>
  );
};

export const ComplexityCallout = ({
  complexity,
  demo,
  color = 'green',
  image,
  isHorizontal,
  children,
}: ComplexityCalloutProps) => {
  const circles = [...Array(5).keys()];
  const intl = useIntl();
  const ariaLabel = intl.formatMessage(messages.complexityAriaLabel, {
    complexity,
  });
  const label = intl.formatMessage(messages.complexity);

  let LeftComponent;
  if (demo === 'elements') {
    if (isHorizontal) {
      LeftComponent = <NewCardPaymentDemo />;
    } else {
      LeftComponent = <PaymentElementDemo />;
    }
  } else if (demo === 'checkout') {
    LeftComponent = <CheckoutPreview image={image} />;
  } else if (image) {
    LeftComponent = <Image src={image} />;
  }

  return (
    <Group
      className="ComplexityCallout"
      direction={isHorizontal ? 'horizontal' : 'vertical'}
      aria-label={ariaLabel}
      style={{padding: isHorizontal ? '32px' : 0}}
    >
      {LeftComponent}
      <Box
        className="ComplexityCallout-Text"
        flex={{direction: 'column'}}
        style={{marginLeft: isHorizontal ? '40px' : '12px'}}
      >
        <ChartIconGroup description={ariaLabel} label={label}>
          {circles.map((i) => (
            <ChartIcon
              key={`circle-${color}-${i}`}
              icon="circle"
              color={i < complexity ? color : 'gray100'}
            />
          ))}
        </ChartIconGroup>
        <Group>{children}</Group>
      </Box>
    </Group>
  );
};
